<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { useRoute } from 'vue-router';
import SettingsBar from '@/components/Shared/LayoutBar/SettingsBar/SettingsBar.vue';

const display = useDisplay();
const isDesktop = computed(() => display.mdAndUp.value);

// The settings sub-nav is a permanent rail on desktop. On mobile it collapses to a
// toggle-able overlay drawer — without this it was unreachable, leaving mobile users
// unable to move between settings pages. Open by default on desktop so the permanent
// rail shows; closed on mobile until the user opens it.
const subNavOpen = ref(isDesktop.value);

// Keep it in sync when crossing the breakpoint (e.g. rotate / resize): rail on desktop,
// hidden overlay on mobile.
watch(isDesktop, (desktop) => {
  subNavOpen.value = desktop;
});

// Close the overlay after navigating on mobile; on desktop the drawer is permanent.
const route = useRoute();
watch(
  () => route.path,
  () => {
    if (!isDesktop.value) {
      subNavOpen.value = false;
    }
  },
);
</script>
<template>
  <div>
    <v-navigation-drawer
      v-model="subNavOpen"
      width="256"
      color="shade-860"
      :permanent="isDesktop"
      :temporary="!isDesktop"
    >
      <SettingsBar />
    </v-navigation-drawer>

    <!-- Mobile-only opener for the settings sub-nav (desktop shows the permanent rail). -->
    <div v-if="!isDesktop" class="flex items-center gap-3 border-b border-tint-100 px-4 py-3">
      <button
        type="button"
        aria-label="Open settings menu"
        class="flex h-10 w-10 items-center justify-center rounded-md hover:bg-tint-40"
        @click="subNavOpen = true"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          aria-hidden="true"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      <span class="font-serif font-bold leading-6">Settings</span>
    </div>

    <!-- The layout owns the vertical lead and the shared 768px width for every
         /settings/* view so they don't each hardcode it. Views own the spacing
         between their own elements. member-profile is the exception: its
         back-link pulls up into this lead on desktop, so the md top value here
         can not drop below 60px without pushing that link outside the wrapper. -->
    <div class="py-10 md:pb-[100px] md:pt-[72px]">
      <div class="m-auto w-full max-w-3xl px-4">
        <RouterView />
      </div>
    </div>
  </div>
</template>
